<script lang="tsx" setup>
import { CirclePlus, Fold, Search } from "@element-plus/icons-vue";
import { ElButton } from "element-plus";
import { computed, onMounted, ref } from "vue";
import {
  Entity,
  ForOwnWorkflowType,
  OrgGroupsDto,
  SysOrgGroup,
  SysOrgRoleName,
} from "~/api";
import { useEntityDefinitionStore } from "~/stores/EntityDefinitionStore";
import { useMyInfoStore } from "~/stores/MyInfoStore";
import { Slotted } from "~/types/Slotted";
import { apiClient } from "~/utils/apiClient";
import { emptyGuid } from "~/utils/constants";
import OrgGroupShell from "./OrgGroupShell.vue";

const entityDefinitionStore = useEntityDefinitionStore();

const myInfoStore = useMyInfoStore();
const orgGroupsInfoDto = ref<OrgGroupsDto>();
const loading = ref(true);
const searchQuery = ref<string>("");

const filteredGroups = computed(() => {
  const searchQueryLowered = searchQuery.value.toLowerCase();
  const searchFiltered =
    searchQueryLowered !== "" && searchQueryLowered !== undefined;

  return (orgGroups.value || []).filter((group: localOrgGroup) => {
    if (!group.name) {
      return;
    }
    if (searchFiltered) {
      const groupNameMatch = group.name
        .toLowerCase()
        .includes(searchQueryLowered);
      if (!groupNameMatch) {
        return false;
      }
    }
    return true;
  });
});
const emit = defineEmits(["update:modelValue"]);

//For the drawer
const drawerOpen = ref(false);
const selectedOrgGroup = ref<SysOrgGroup | null>(null);
const isAddingNewOrgGroup = ref(false);

let retrievingOrgGroups = false;

const retrieveOrgGroups = async () => {
  if (!myInfoStore.currentSysOrgGuid) {
    return;
  }
  if (retrievingOrgGroups) {
    // may be called twice in a row
    return;
  }
  retrievingOrgGroups = true;
  orgGroupsInfoDto.value = await apiClient.orgGroup.getOrgGroupsInfo(
    myInfoStore.currentSysOrgGuid,
  );
  retrievingOrgGroups = false;
};

const orgGroups = computed(() =>
  orgGroupsInfoDto.value?.orgGroups?.map(extendGroup),
);

type localOrgGroup = SysOrgGroup & {
  entityName?: string;
  entityTypeName?: string;
  entityTypeIcon?: string;
};

const extendGroup = (group: SysOrgGroup): localOrgGroup => {
  return {
    ...group,
    // Add local properties
    entityName: undefined,
    entityTypeName: undefined,
    entityTypeIcon: undefined,
  };
};

const showViewer = (row: SysOrgGroup) => {
  if (!canEdit.value) {
    return;
  }
  selectedOrgGroup.value = row;
  drawerOpen.value = true;
  isAddingNewOrgGroup.value = false;
};

const canEdit = ref(false);

const reloadData = async () => {
  loading.value = true;
  // done in order... myInfoStore must be loaded first
  await myInfoStore.load();

  canEdit.value = myInfoStore.currentSysOrg.roles.includes(
    SysOrgRoleName.USER_ADMIN,
  );

  await retrieveOrgGroups();
  loading.value = false;
};

const handleCloseViewer = () => {
  selectedOrgGroup.value = null;
  drawerOpen.value = false;
  reloadData();
};

const handleCreateGroup = () => {
  if (!myInfoStore.currentSysOrgGuid) {
    return;
  }
  selectedOrgGroup.value = {
    guid: emptyGuid,
    sysOrgGuid: myInfoStore.currentSysOrgGuid,
    name: "",
  };
  drawerOpen.value = true;
  isAddingNewOrgGroup.value = true;
};

const getEntityNames = (entities: Entity[]) => {
  return (entities ?? []).sort((a: Entity, b: Entity) =>
    (a.name ?? "").localeCompare(b.name ?? ""),
  );
};

// getName function removed - now using entity.name directly from the entities array

onMounted(async () => {
  await entityDefinitionStore.load();

  reloadData();
});
</script>

<template>
  <div class="org-groups-list">
    <div v-if="!loading" class="flexRow">
      <el-input
        v-model="searchQuery"
        clearable
        placeholder="Search"
        :prefix-icon="Search"
      />
      <span class="showingInTable">
        Showing {{ filteredGroups.length }} of
        {{ orgGroups?.length }}
      </span>
      <RefreshButton @click="reloadData" />
      <el-button
        v-if="canEdit"
        class="addButton"
        :icon="CirclePlus"
        @click="handleCreateGroup"
      >
        New Security Group
      </el-button>
    </div>

    <el-table
      v-loading="loading"
      :data="filteredGroups"
      class="GroupsTable"
      height="90vh"
      :default-sort="{ prop: 'name', order: 'ascending' }"
    >
      <el-table-column
        label="Security Group"
        align="left"
        sortable
        prop="name"
        width="300"
      >
        <template #default="scope">
          <el-link
            :icon="Fold"
            type="primary"
            style="font-size: 15px"
            underline="never"
            @click="showViewer(scope.row)"
          >
            {{ scope.row.name }}
          </el-link>
        </template>
      </el-table-column>
      <el-table-column label="Entities in Group" align="left">
        <template #default="scope">
          <div class="user-tags">
            <el-tag
              v-for="entity in getEntityNames(scope.row.entities)"
              :key="entity.guid"
            >
              {{ entity.name }}
            </el-tag>
          </div>
        </template>
      </el-table-column>
      <el-table-column
        prop="forOwnWorkflow"
        label="For Own Workflow"
        align="left"
      >
        <template #default="{row}: Slotted<SysOrgGroup>">
          <el-tag v-if="row.forOwnWorkflow === ForOwnWorkflowType.OWN"
            >Yes</el-tag
          >
          <el-tag v-else-if="row.forOwnWorkflow === ForOwnWorkflowType.OTHERS"
            >No - only for others</el-tag
          >
          <span v-else>n/a</span>
        </template>
      </el-table-column>
    </el-table>
    <OrgGroupShell
      v-if="selectedOrgGroup && orgGroupsInfoDto?.entities"
      :key="selectedOrgGroup?.guid"
      :org-group="selectedOrgGroup"
      :drawer-open="drawerOpen"
      :available-entities="orgGroupsInfoDto?.entities"
      :adding-new-org-group="isAddingNewOrgGroup"
      @drawer-closed="handleCloseViewer"
      @added="reloadData"
      @updated="reloadData"
    />
  </div>
</template>

<style lang="less">
.org-groups-list {
  .edit-buttons {
    grid-area: edit;
    display: flex;
    gap: 1em;
  }
  .el-tag {
    font-size: 1em;
  }

  table {
    width: 100%;
    td {
      padding: 0.5em;
      border-bottom: 1px solid #ebeef5;
    }
  }
  .user-tags {
    display: flex;
    gap: 0.5em;
    flex-wrap: wrap;
  }
}
</style>
